import { graphql, useStaticQuery } from 'gatsby';
import { includes } from 'ramda';
import React, { FC, useContext } from 'react';
import { useDeepCompareEffect } from 'react-use';

import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import { doesNotExist } from '@utils/general';

import FlowContext from '@providers/FlowContextProvider';

import { medicaidStates } from '@data/US_States';

import { IFlowBailoutStepProps } from '@folx-types/flow-steps';

import MedicaidIneligibilityStep, {
	IMedicaidIneligibilityStepContent,
} from './MedicaidIneligibilityStep.component';

interface IMedicaidIneligibilityStepPageProps extends IFlowBailoutStepProps {}

const AlignedRow = aligned(FlexRow);
const FullWidthColumn = fullWidth(FlexColumn);

const PageSkeleton = () => (
	<FullWidthColumn gap="xl">
		<FlexColumn gap="md">
			<Skeleton structure="column" height="lg" repeated={4} gap="xs" fullWidth />
			<Skeleton structure="column" repeated={7} gap="xs" height="sm" fullWidth />
		</FlexColumn>
		<AlignedRow gap="sm" alignItems="center">
			<Skeleton height="lg" fullWidth />
			<Skeleton height="lg" fullWidth />
		</AlignedRow>
	</FullWidthColumn>
);

const MedicaidIneligibilityStepPage: FC<IMedicaidIneligibilityStepPageProps> = ({
	path,
	stepKey,
	...props
}) => {
	const { pageContent } = useStaticQuery<{
		pageContent: IMedicaidIneligibilityStepContent;
	}>(graphql`
		{
			pageContent: butterPage(slug: { eq: "membership-medicaid-ineligibility-step-page" }) {
				title
				description
			}
		}
	`);

	const context = useContext(FlowContext);
	const { handleNavBack, handleNavNext, existingFormData } = context;

	const stateOfResidence =
		existingFormData?.stateOfResidence ?? existingFormData?.shippingState ?? '';

	useDeepCompareEffect(() => {
		if (doesNotExist(stateOfResidence)) {
			return;
		}

		const isMedicaidState = includes(stateOfResidence, medicaidStates);
		if (!isMedicaidState) {
			props?.location?.state?.reverse ? handleNavBack(stepKey) : handleNavNext(stepKey);
		}
	}, [stateOfResidence]);

	return (
		<LoadingWrapper loading={doesNotExist(stateOfResidence)} skeleton={<PageSkeleton />}>
			<MedicaidIneligibilityStep
				pageContent={pageContent}
				path={path}
				stateOfResidence={stateOfResidence}
				stepKey={stepKey}
			/>
		</LoadingWrapper>
	);
};

export default MedicaidIneligibilityStepPage;
